import React from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";

const buildFallbackOption = (planCode) => {
  if (!planCode) return null;

  return {
    value: planCode,
    label: `Current selection: ${planCode}`,
    meta: {
      name: "Current selection",
      plan_code: planCode,
      interval: "unknown",
      amountLabel: "Unavailable",
      description: null,
    },
  };
};

const PaystackPlanAutocomplete = ({
  label,
  fieldName,
  value,
  options,
  isLoading,
  handleInputChange,
  placeholder,
}) => {
  const selectedOption =
    options.find((option) => option.value === value) ||
    buildFallbackOption(value);

  const handleSelectionChange = (_event, option) => {
    handleInputChange({
      target: {
        name: fieldName,
        value: option?.value || "",
        type: "text",
      },
    });
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleSelectionChange}
      loading={isLoading}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected?.value
      }
      getOptionLabel={(option) => option?.label || ""}
      noOptionsText="No Paystack plans found"
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ py: 1.25, alignItems: "stretch" }}>
          <Box
            sx={{
              width: "100%",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              px: 1.5,
              py: 1.25,
              bgcolor: "#FFFFFF",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#0F172A" }}
              >
                {option.meta?.name || option.label}
              </Typography>
            </Box>
            <div className="flex items-center justify-between">
              <Chip
                label={option.meta?.interval || "unknown"}
                size="small"
                sx={{
                  textTransform: "capitalize",
                  bgcolor: "#EFF6FF",
                  color: "#1D4ED8",
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "#334155" }}
              >
                {option.amountLabel || option.meta?.amountLabel || option.label}
              </Typography>
            </div>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          helperText="Search and select one Paystack plan"
          InputLabelProps={{ shrink: true }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#fff",
            },
          }}
        />
      )}
    />
  );
};

const BasicInformationSection = ({
  formData,
  handleInputChange,
  paystackPlanOptions = [],
  isPaystackPlansLoading = false,
  errors = {},
}) => {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
        Basic Information
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-medium">Plan Name</label>
          <input
            type="text"
            name="name"
            value={formData.name || ""}
            onChange={handleInputChange}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
              errors.name
                ? "border-red-400 focus:ring-red-200"
                : "focus:ring-blue-500"
            }`}
            required
          />
          {errors.name ? (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          ) : null}
        </div>

        <div>
          <label className="block mb-1 font-medium">Mode</label>
          <select
            name="mode"
            value={formData.mode || "Free"}
            onChange={handleInputChange}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Free">Free</option>
            <option value="Interval">Interval</option>
            <option value="Tiered">Tiered</option>
            <option value="PAYG">PAYG</option>
          </select>
        </div>

        <div>
          <PaystackPlanAutocomplete
            label="Paystack ID"
            fieldName="paystackId"
            value={formData.paystackId || ""}
            options={paystackPlanOptions}
            isLoading={isPaystackPlansLoading}
            handleInputChange={handleInputChange}
            placeholder="Select monthly or interval plan"
          />
        </div>

        <div>
          <PaystackPlanAutocomplete
            label="Annual PlanId"
            fieldName="annualPlanId"
            value={formData.annualPlanId || ""}
            options={paystackPlanOptions}
            isLoading={isPaystackPlansLoading}
            handleInputChange={handleInputChange}
            placeholder="Select annual or alternate plan"
          />
        </div>

        <div className="col-span-2">
          <label className="block mb-1 font-medium">Description</label>
          <textarea
            name="description"
            value={formData.description || ""}
            onChange={handleInputChange}
            rows="3"
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
              errors.description
                ? "border-red-400 focus:ring-red-200"
                : "focus:ring-blue-500"
            }`}
            required
          />
          {errors.description ? (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BasicInformationSection;
